import clsx from "clsx";
import { useState } from "react";
import Button from "_shared/components/Button/Button";
import Loading from "_shared/components/Loading/Loading";
import CustomModal from "_shared/components/Modal/Modal";
import {
  Ticker,
  useDeleteUserTickersMutation,
  UserTickersDocument,
} from "_shared/graphql";
import styles from "./UserTickersModal.module.scss";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  userTickers: Ticker[];
  tickerLimit: number;
}

const UserTickersModal: React.FC<Props> = ({
  isOpen,
  onClose,
  userTickers,
  tickerLimit,
}) => {
  const [deleteUserTickers] = useDeleteUserTickersMutation();

  const [deleteTickers, setDeleteTickers] = useState<Ticker[]>([]);
  const [loading, setLoading] = useState(false);

  const tickersToRemove =
    userTickers.length - deleteTickers.length - tickerLimit;

  const subtitle =
    userTickers.length > tickerLimit
      ? tickersToRemove > 0
        ? `Remove ${tickersToRemove} tickers for the downgrade to proceed:`
        : "Save your ticker list for the downgrade to proceed:"
      : "Current list of tickers in your custom ticker panels:";

  return (
    <CustomModal
      open={isOpen}
      onClose={onClose}
      title="Tickers"
      subtitle={subtitle}
    >
      <div className={styles.container}>
        {loading ? (
          <Loading variant="modal" />
        ) : (
          <>
            {!userTickers.length ? (
              "No tickers have been added to your custom panels."
            ) : (
              <div>
                {/* User Tickers Table */}
                <div className={styles.gridWrapper}>
                  <div>Ticker</div>
                  <div>Panel</div>
                </div>
                <div className={clsx(styles.gridWrapper, styles.scrollList)}>
                  {/* Map filtered ticker list for the first table column. */}
                  {userTickers.map((ticker, i) => {
                    const deletedTickerNames = deleteTickers.map(
                      (ticker) => ticker.ticker,
                    );
                    const isDeleting = deletedTickerNames.includes(
                      ticker.ticker,
                    );
                    return (
                      <>
                        <span
                          id={String(i)}
                          className={styles.tickerBubble}
                          style={{
                            cursor: !isDeleting ? "pointer" : "not-allowed",
                            opacity: !isDeleting ? "100%" : "35%",
                          }}
                          onClick={(e) => {
                            // do nothing if ticker is already set to delete
                            if (isDeleting) return;
                            setDeleteTickers([...deleteTickers, ticker]);
                          }}
                        >
                          {ticker.ticker}
                          <button className={styles.closeButton}>x</button>
                        </span>
                        <div>{ticker.panel.name}</div>
                      </>
                    );
                  })}
                </div>
              </div>
            )}
            {deleteTickers.length > 0 ? (
              <div className={styles.removeSection}>
                <h4>You are removing:</h4>
                <div className={styles.bubbleGrid}>
                  {deleteTickers.map((ticker) => (
                    <div
                      key={ticker.id}
                      className={styles.tickerBubble}
                      onClick={(e) => {
                        setDeleteTickers(
                          deleteTickers.filter(
                            (ticker) =>
                              ticker !==
                              deleteTickers[Number(e.currentTarget.id)],
                          ),
                        );
                      }}
                    >
                      {ticker.ticker}
                      <button
                        className={styles.closeButton}
                        style={{
                          color: "#d33541",
                          fontSize: "10px",
                        }}
                      >
                        <i className="fas fa-undo-alt"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            <div className="footer-buttons">
              <Button onClick={onClose} variant="secondary">
                Cancel
              </Button>
              <Button
                disabled={!deleteTickers.length}
                onClick={async () => {
                  setLoading(true);
                  await deleteUserTickers({
                    variables: {
                      tickers: deleteTickers.map((ticker) => ticker.ticker),
                    },
                    refetchQueries: [UserTickersDocument],
                    onCompleted: () => {
                      setDeleteTickers([]);
                      setLoading(false);
                    },
                  });
                }}
                loading={loading}
              >
                Save
              </Button>
            </div>
          </>
        )}
      </div>
    </CustomModal>
  );
};

export default UserTickersModal;
